'use client';

import {
  useContext,
  createContext,
  PropsWithChildren,
  Key,
  Dispatch,
  SetStateAction,
} from 'react';

const DallasTableSelectionMapContext = createContext<Map<Key, any> | null>(
  null,
);

const DallasTableSelectionChangeContext = createContext<Dispatch<
  SetStateAction<Map<Key, any>>
> | null>(null);

const DallasTableGetDataItemKeyContext = createContext<
  ((dataItem: any) => Key) | null
>(null);

export function useGetDataItemKey() {
  const getDataItemKey = useContext(DallasTableGetDataItemKeyContext);
  if (getDataItemKey === null) {
    throw new Error(
      'useGetDataItemKey has to be used within <DallasTableSelectionProvider>',
    );
  }
  return getDataItemKey;
}

export function useRowSelectionState<T>(key: Key, data: T) {
  const selectionMap = useContext(DallasTableSelectionMapContext);
  const setSelectionMap = useContext(DallasTableSelectionChangeContext);
  if (selectionMap === null || setSelectionMap === null) {
    throw new Error(
      'useRowSelectionState has to be used within <DallasTableSelectionProvider>',
    );
  }

  const selected = selectionMap.has(key);

  const toggle = () => {
    if (selected) {
      selectionMap.delete(key);
    } else {
      selectionMap.set(key, data);
    }
    setSelectionMap(new Map(selectionMap));
  };

  return {
    selected,
    toggle,
  };
}

export function useTableSelectionState<T>(data: T[]) {
  const selectionMap = useContext(DallasTableSelectionMapContext);
  const setSelectionMap = useContext(DallasTableSelectionChangeContext);
  const getDataItemKey = useContext(DallasTableGetDataItemKeyContext);
  if (
    selectionMap === null ||
    setSelectionMap === null ||
    getDataItemKey === null
  ) {
    throw new Error(
      'useTableSelectionState has to be used within <DallasTableSelectionProvider>',
    );
  }

  const isAllSelected = selectionMap.size === data.length;

  const isNoneSelected = selectionMap.size === 0;

  const selected: boolean | 'mixed' = isAllSelected
    ? true
    : isNoneSelected
    ? false
    : 'mixed';

  const toggle = () => {
    if (isAllSelected) {
      setSelectionMap(new Map());
    } else {
      setSelectionMap(
        new Map(data.map((item) => [getDataItemKey(item), item])),
      );
    }
  };

  return {
    toggle,
    selected,
  };
}

interface Props<T> {
  selectionMap: Map<Key, T>;
  onSelectionChange: Dispatch<SetStateAction<Map<Key, T>>>;
  getDataItemKey: (dataItem: T) => Key;
}

export default function DallasTableSelectionProvider<T>({
  selectionMap,
  onSelectionChange,
  getDataItemKey,
  children,
}: PropsWithChildren<Props<T>>) {
  return (
    <DallasTableSelectionMapContext.Provider value={selectionMap}>
      <DallasTableSelectionChangeContext.Provider value={onSelectionChange}>
        <DallasTableGetDataItemKeyContext.Provider value={getDataItemKey}>
          {children}
        </DallasTableGetDataItemKeyContext.Provider>
      </DallasTableSelectionChangeContext.Provider>
    </DallasTableSelectionMapContext.Provider>
  );
}
